// src/features/rrhh/dashboard/components/RrhhSectionsGrid.jsx
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AssignmentIcon from "@mui/icons-material/Assignment";
import BusinessIcon from "@mui/icons-material/Business";
import MessageIcon from "@mui/icons-material/Message";
import TheaterComedyIcon from '@mui/icons-material/TheaterComedy';
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Box, ButtonBase, Stack, Typography } from "@mui/material";

const defaultAreasData = [
  {
    icon: <BusinessIcon />,
    label: "Nómina",
    description: "Gestión de empleados, información personal y profesional.",
    to: "/rrhh/empleados",
  },
  {
    icon: <AccessTimeIcon />,
    label: "Horarios",
    description: "Administra horarios y jornadas laborales del personal.",
    to: "/rrhh/horarios",
  },
  {
    icon: <HowToRegIcon />,
    label: "Asistencia",
    description:
      "Revisa y gestiona las asistencias, ausencias y horas extras del equipo.",
    to: "/rrhh/asistencia",
  },
  {
    icon: <AssignmentIcon />,
    label: "Tareas",
    description:
      "Configurá rutinas por rol, creá tareas puntuales y seguí el cumplimiento diario del equipo.",
    to: "/rrhh/tareas",
  },
  {
    icon: <AccountBalanceIcon />,
    label: "Liquidación",
    description:
      "Administra sueldos, bonificaciones, descuentos y genera recibos de sueldo para el equipo.",
    to: "/rrhh/liquidacion",
  },
  {
    icon: <MessageIcon />,
    label: "Mensajería",
    description:
      "Comunícate con el equipo, envía anuncios, mensajes directos con personal y administra reclamos de los mismos.",
    to: "/rrhh/mensajes",
  },
  {
    icon: <TheaterComedyIcon />,
    label: "Eventos",
    description: "Gestiona y visualiza los eventos de la empresa.",
    to: "/rrhh/eventos",
  },
  {
    icon: <ContentPasteIcon />,
    label: "Contenido",
    description:
      "Administra documentos, comunicados, manuales, políticas y contenido institucional.",
    to: "/rrhh/contenido",
  },
];

export default function RrhhSectionsGrid({
  areas = defaultAreasData,
  onAreaClick,
}) {
  return (
    <Stack
      spacing={1.5}
      sx={{
        p: 2,
        bgcolor: "rgba(244, 245, 251, 0.88)",
        borderRadius: "24px",
        border: "1px solid rgba(255,255,255,0.8)",
        boxShadow:
          "inset 0px 0px 5px rgba(117, 117, 138, 0.25), 0px 10px 28px rgba(27, 37, 89, 0.08)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          width: "fit-content",
          px: 2,
          py: 1,
          bgcolor: "primary.500",
          borderRadius: "33px",
          boxShadow:
            "inset 0px 0px 3px rgba(192, 192, 201, 0.5), 0px 5px 14px rgba(16, 47, 148, 0.28)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 1.5,
        }}
      >
        <Typography
          sx={{
            color: "logoblanco",
            fontSize: 18,
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          Áreas a acceder
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(5, minmax(0, 1fr))",
          },
          gap: 2,
          alignItems: "stretch",
        }}
      >
        {areas.map((area) => (
          <ButtonBase
            key={area.label}
            onClick={onAreaClick ? () => onAreaClick(area) : undefined}
            sx={{
              width: "100%",
              minHeight: { xs: 220, lg: 190 },
              borderRadius: "20px",
              textAlign: "left",
              alignItems: "stretch",
              justifyContent: "stretch",
              bgcolor: "rgba(255,255,255,0.78)",
              border: "1px solid rgba(255,255,255,0.92)",
              boxShadow:
                "0px 8px 20px rgba(27, 37, 89, 0.08), inset 0px 0px 3px rgba(142, 142, 159, 0.18)",
              overflow: "hidden",
              position: "relative",
              transition:
                "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease, background 180ms ease",
              "&::before": {
                content: '""',
                position: "absolute",
                inset: 0,
                background:
                  "radial-gradient(circle at 18% 15%, rgba(29, 70, 183, 0.10), transparent 32%)",
                opacity: 0,
                transition: "opacity 180ms ease",
              },
              "&:hover": {
                transform: "translateY(-6px)",
                bgcolor: "rgba(248, 250, 255, 0.96)",
                borderColor: "rgba(35, 78, 190, 0.35)",
                boxShadow:
                  "0px 14px 30px rgba(27, 37, 89, 0.16), inset 0px 0px 3px rgba(142, 142, 159, 0.18)",
              },
              "&:hover::before": {
                opacity: 1,
              },
              "&:hover .areaIcon": {
                transform: "scale(1.06) translateY(-2px)",
                bgcolor: "rgba(30, 68, 177, 0.13)",
              },
              "&:hover .areaTitle": {
                color: "primary.700",
                transform: "translateY(-2px)",
              },
              "&:hover .areaDescription": {
                opacity: 1,
                transform: "translateY(0)",
              },
              "&:hover .areaArrow": {
                bgcolor: "primary.600",
                color: "#fff",
                transform: "translateX(4px)",
                boxShadow: "0px 8px 18px rgba(16, 47, 148, 0.32)",
              },
              "&:hover .areaBottomLine": {
                width: "36%",
                opacity: 1,
              },
            }}
          >
            <Box
              sx={{
                width: "100%",
                height: "100%",
                p: 2,
                display: "flex",
                flexDirection: "column",
                position: "relative",
                zIndex: 1,
              }}
            >
              <Box
                className="areaIcon"
                sx={{
                  width: 46,
                  height: 46,
                  borderRadius: "50%",
                  bgcolor: "rgba(30, 68, 177, 0.10)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "primary.700",
                  mb: 2,
                  boxShadow: "inset 0px 0px 4px rgba(30, 68, 177, 0.08)",
                  transition: "transform 180ms ease, background 180ms ease",
                  "& svg": {
                    width: 32,
                    height: 32,
                  },
                }}
              >
                {area.icon}
              </Box>

              <Typography
                className="areaTitle"
                sx={{
                  fontWeight: 800,
                  color: "rgba(18, 27, 61, 0.95)",
                  fontSize: 16,
                  lineHeight: 1,
                  mb: 1,
                  textTransform: "none",
                  transition: "transform 180ms ease, color 180ms ease",
                }}
              >
                {area.label}
              </Typography>

              <Typography
                className="areaDescription"
                sx={{
                  fontWeight: 500,
                  color: "rgba(19, 27, 58, 0.82)",
                  fontSize: 13,
                  lineHeight: 1.2,
                  opacity: 0.9,
                  transform: "translateY(2px)",
                  transition: "opacity 180ms ease, transform 180ms ease",
                }}
              >
                {area.description}
              </Typography>

              <Box sx={{ flexGrow: 1 }} />

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  mt: 0,
                }}
              >
                <Box
                  className="areaArrow"
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    bgcolor: "rgba(255,255,255,0.9)",
                    color: "primary.800",
                    border: "1px solid rgba(20, 32, 82, 0.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition:
                      "transform 180ms ease, background 180ms ease, color 180ms ease, box-shadow 180ms ease",
                  }}
                >
                  <ArrowForwardIcon sx={{ fontSize: 23 }} />
                </Box>
              </Box>

              <Box
                className="areaBottomLine"
                sx={{
                  position: "absolute",
                  left: 22,
                  bottom: 0,
                  width: 0,
                  height: 4,
                  borderRadius: "99px 99px 0 0",
                  bgcolor: "primary.600",
                  opacity: 0,
                  transition: "width 180ms ease, opacity 180ms ease",
                }}
              />
            </Box>
          </ButtonBase>
        ))}
      </Box>
    </Stack>
  );
}
